import type { ShapeInsertKind } from '@/components/canvas/elements/shape/toolbar/shapeInsertKinds';

/** 拖拽绘制时的最小有效尺寸（画布像素），仅用于 mouseup 落盘 */
export const MIN_SHAPE_DRAW_SIZE = 20;

export interface ShapeBoxRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ShapeLineSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export type ShapeDrawPreview = {
  kind: ShapeInsertKind;
} & (
  | { type: 'line'; segment: ShapeLineSegment }
  | { type: 'box'; rect: ShapeBoxRect }
);

/** Shift：锁定水平或垂直 */
export function constrainLineEndpoint(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { x2: number; y2: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return { x2, y2: y1 };
  }
  return { x2: x1, y2 };
}

/** 拖拽预览：按实际拖拽框，不使用默认尺寸 */
export function previewBoxFromDrag(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
): ShapeBoxRect {
  return {
    x: Math.min(sx, ex),
    y: Math.min(sy, ey),
    width: Math.max(1, Math.abs(ex - sx)),
    height: Math.max(1, Math.abs(ey - sy)),
  };
}

export function previewSquareFromDrag(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
): ShapeBoxRect {
  const box = previewBoxFromDrag(sx, sy, ex, ey);
  const size = Math.max(box.width, box.height);
  return { x: box.x, y: box.y, width: size, height: size };
}

/** mouseup 落盘：过小则用默认尺寸 */
export function boxFromDrag(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  defaults: { width: number; height: number },
): ShapeBoxRect {
  let x = Math.min(sx, ex);
  let y = Math.min(sy, ey);
  let width = Math.abs(ex - sx);
  let height = Math.abs(ey - sy);
  if (width < MIN_SHAPE_DRAW_SIZE && height < MIN_SHAPE_DRAW_SIZE) {
    return { x: sx, y: sy, width: defaults.width, height: defaults.height };
  }
  if (width < MIN_SHAPE_DRAW_SIZE) width = MIN_SHAPE_DRAW_SIZE;
  if (height < MIN_SHAPE_DRAW_SIZE) height = MIN_SHAPE_DRAW_SIZE;
  return { x, y, width, height };
}

export function squareBoxFromDrag(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  defaultSize: number,
): ShapeBoxRect {
  const raw = boxFromDrag(sx, sy, ex, ey, { width: defaultSize, height: defaultSize });
  const size = Math.max(raw.width, raw.height);
  return { x: raw.x, y: raw.y, width: size, height: size };
}

/** 矩形：单击落正方形，拖拽为任意长宽比 */
export function rectBoxFromDrag(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  defaultSquareSize: number,
): ShapeBoxRect {
  const width = Math.abs(ex - sx);
  const height = Math.abs(ey - sy);
  if (width < MIN_SHAPE_DRAW_SIZE && height < MIN_SHAPE_DRAW_SIZE) {
    return { x: sx, y: sy, width: defaultSquareSize, height: defaultSquareSize };
  }
  return boxFromDrag(sx, sy, ex, ey, {
    width: defaultSquareSize,
    height: defaultSquareSize,
  });
}

export function lineFromDrag(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  shiftKey: boolean,
  defaultLength: number,
): { x: number; y: number; endX: number; endY: number; width: number; height: number } {
  let x2 = ex;
  let y2 = ey;
  if (shiftKey) {
    ({ x2, y2 } = constrainLineEndpoint(sx, sy, x2, y2));
  }
  let endX = x2 - sx;
  let endY = y2 - sy;
  const len = Math.hypot(endX, endY);
  if (len < MIN_SHAPE_DRAW_SIZE) {
    endX = defaultLength;
    endY = 0;
  }
  const width = Math.max(1, Math.abs(endX));
  const height = Math.max(1, Math.abs(endY));
  return { x: sx, y: sy, endX, endY, width, height };
}

export function previewFromDrag(
  kind: ShapeInsertKind,
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  shiftKey: boolean,
): ShapeDrawPreview {
  if (kind === 'line') {
    let x2 = ex;
    let y2 = ey;
    if (shiftKey) {
      ({ x2, y2 } = constrainLineEndpoint(sx, sy, x2, y2));
    }
    return { kind, type: 'line', segment: { x1: sx, y1: sy, x2, y2 } };
  }
  const rect =
    kind === 'circle'
      ? previewSquareFromDrag(sx, sy, ex, ey)
      : previewBoxFromDrag(sx, sy, ex, ey);
  return { kind, type: 'box', rect };
}
